import React, { useState } from "react";
import PropTypes from "kf-proptypes/widgets";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./dropdowntarget.css";

export const DROPDOWNTARGET_SIZE = {
  MEDIUM_DEPRECATED: "mediumDeprecated",
  SMALL_DEPRECATED: "smallDeprecated",
  BASE: "base",
  MEDIUM: "medium",
  SMALL: "small"
};

export const DROPDOWNTARGET_TYPE = {
  ARROW: "arrow",
  MORE: "more",
  NONE: "none",
  MINIMAL_ARROW: "minimalArrow",
  MINIMAL_MORE: "minimalMore",
  MINIMAL_NONE: "minimalNone"
};

/**
 * Dropdown Target component is used as trigger point to open the custom dropdown of the user
 */
export function DropdownTarget(props) {
  const {
    isOpen = false,
    id,
    label,
    children,
    className,
    size = DROPDOWNTARGET_SIZE.BASE,
    type = DROPDOWNTARGET_TYPE.ARROW,
    onSearch,
    onClick,
    onLabelClick,
    onArrowClick,
    onClear,
    readOnly = false,
    disabled = false,
    searchText = "",
    placeholder = ""
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  return (
    <div>
      {label && (
        <label
          className={`${styles.label} small`}
          htmlFor={id}
          id={`${id}_label`}
        >
          {label}
        </label>
      )}
      <div
        className={`${styles.dropdowntarget} ${isOpen ? styles.open : ""} ${
          styles[size]
        } ${styles[type]} ${className} ${
          size === DROPDOWNTARGET_SIZE.SMALL_DEPRECATED ? `smallplus` : ""
        } ${readOnly && styles.readOnly} ${disabled && styles.disabled}`}
        onClick={onClick}
        tabIndex={0}
        onFocus={() => {
          if (onSearch) setIsEditing(true);
        }}
        id={id}
      >
        <div className={`${styles.content}`} onClick={onLabelClick}>
          {onSearch && isEditing ? (
            <input
              type="text"
              className={styles.editable}
              autoFocus={true}
              onBlur={() => {
                setIsEditing(false);
              }}
              onChange={(e) => {
                onSearch(e.target.value);
              }}
              placeholder={placeholder}
              {...(searchText && { value: searchText })}
            />
          ) : (
            children
          )}
        </div>
        {!!onClear && (
          <div className={styles.clear}>
            <KfIcon
              icon={KF_ICONS.CLOSE}
              size={ICON_SIZE.SIZE_12}
              stroke={ICON_STROKE.REGULAR}
              onClick={onClear}
            />
          </div>
        )}
        {!readOnly &&
          !disabled &&
          (type === DROPDOWNTARGET_TYPE.ARROW ||
            type === DROPDOWNTARGET_TYPE.MINIMAL_ARROW) && (
            <div className={styles.arrow} onClick={onArrowClick}>
              <KfIcon
                icon={KF_ICONS.ARROW_DOWN_SIMPLE}
                stroke={ICON_STROKE.REGULAR}
                size={ICON_SIZE.SIZE_12}
              />
            </div>
          )}
        {!readOnly &&
          !disabled &&
          (type === DROPDOWNTARGET_TYPE.MORE ||
            type === DROPDOWNTARGET_TYPE.MINIMAL_MORE) && (
            <div className={styles.more} onClick={onArrowClick}>
              <KfIcon
                icon={KF_ICONS.MORE_HORIZONTAL}
                size={ICON_SIZE.SIZE_12}
                stroke={ICON_STROKE.LIGHT}
              />
            </div>
          )}
      </div>
    </div>
  );
}

DropdownTarget.propTypes = {
  /**
   * Default open the dropdown
   */
  isOpen: PropTypes.bool,
  /**
   * Target dropdown children component to render
   */
  children: PropTypes.node.isRequired,
  /**
   * Should be of Type DROPDOWNTARGET_SIZE
   */
  size: PropTypes.oneOf(Object.values(DROPDOWNTARGET_SIZE)),
  /**
   * Trigger if user search in dropdown target
   */
  onSearch: PropTypes.func,
  /**
   * Different type of dropdown targert
   */
  type: PropTypes.oneOf(Object.values(DROPDOWNTARGET_TYPE)),
  /**
   * Trigger if user clicks in dropdown target
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Trigger if user click label in dropdown target
   */
  onLabelClick: PropTypes.func,
  /**
   * Trigger if user click arrow in dropdown target
   */
  onArrowClick: PropTypes.func,
  /**
   * Trigger if user click clear icon in dropdown target
   */
  onClear: PropTypes.func,
  /**
   * Style the dropdown target
   */
  className: PropTypes.string,
  /**
   * To make the dropdown target as readonly
   */
  readOnly: PropTypes.bool,
  /**
   * To make the dropdown target as disabled
   */
  disabled: PropTypes.bool,
  /**
   * Search placeholder text
   */
  searchText: PropTypes.string,
  /**
   * Label of the dropdown target
   */
  label: PropTypes.string,
  id: PropTypes.string,
  /**
   * Placeholder of the dropdown target
   */
  placeholder: PropTypes.string
};

DropdownTarget.defaultProps = {
  isOpen: false,
  size: DROPDOWNTARGET_SIZE.BASE,
  type: DROPDOWNTARGET_TYPE.ARROW,
  readOnly: false,
  disabled: false
};
